// src/components/InviteLinkModal.tsx
import React, { useState } from 'react';
import { 
  Button, 
  Flex, 
  Text, 
  View, 
  Heading,
  TextField,
  Card,
  Divider,
  CheckboxField
} from '@aws-amplify/ui-react';
import { Link, Copy, Check, Share2, X } from 'lucide-react';
import { TripRole } from '../API';
import { createTripInvitation } from '../services/invitationService';

interface InviteLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  tripName: string;
}

const InviteLinkModal: React.FC<InviteLinkModalProps> = ({ 
  isOpen, 
  onClose, 
  tripId,
  tripName 
}) => {
  const [selectedRole, setSelectedRole] = useState<TripRole>(TripRole.PARTICIPANT);
  const [linkGenerated, setLinkGenerated] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const generateMagicLink = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await createTripInvitation(tripId, selectedRole);
      setMagicLink(response.link);
      setLinkGenerated(true);
    } catch (err) {
      console.error('Error generating invitation link:', err);
      setError('Failed to generate invitation link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(magicLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: `Join my album "${tripName}" on Banjara`,
        text: `I've invited you to join my album "${tripName}" on Banjara`,
        url: magicLink,
      }).catch(err => console.error('Error sharing:', err));
    } else {
      copyLinkToClipboard();
    }
  };

  const resetModal = () => {
    setLinkGenerated(false);
    setMagicLink('');
    setLinkCopied(false);
    setError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    }}
  >
      <Card 
      style={{
        maxWidth: '500px',
        width: '100%',
        backgroundColor: '#1A1A1A',
        boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.5)',
        borderRadius: '8px',
        position: 'relative', // Ensure positioning context for children
        margin: '0 auto' // Center horizontally
      }}
    >
        {/* Header */}
        <Flex direction="row" justifyContent="space-between" alignItems="center" padding="1rem">
          <Heading level={4} color="white" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em">
            {linkGenerated ? 'Share Invitation Link' : 'Create Invitation Link'}
          </Heading>
          <Button
            variation="link"
            onClick={handleClose}
          >
            <X size={20} className="text-gray-400 hover:text-white" />
          </Button>
        </Flex>
        
        <Divider />
        
        {/* Body */}
        <View padding="1.5rem">
          {!linkGenerated ? (
            <>
              <Text color="white" marginBottom="1.5rem">
                Create a magic link that allows anyone to join this album. 
              </Text>
              
              <View marginBottom="1.5rem">
                <Text 
                  color="#A0AEC0" 
                  fontSize="0.75rem" 
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  marginBottom="0.75rem"
                >
                  SET PERMISSION LEVEL FOR LINK
                </Text>
                
                {/* Custom radio selection */}
                <Flex direction="column" gap="0.75rem">
                  <View 
                    padding="1rem"
                    backgroundColor={selectedRole === TripRole.PARTICIPANT ? "#1E3A8A" : "rgba(45, 55, 72, 0.5)"}
                    borderRadius="medium"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor={selectedRole === TripRole.PARTICIPANT ? "#3B82F6" : "#4A5568"}
                    onClick={() => setSelectedRole(TripRole.PARTICIPANT)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Flex direction="row" alignItems="center">
                      <CheckboxField
                        checked={selectedRole === TripRole.PARTICIPANT}
                        onChange={() => setSelectedRole(TripRole.PARTICIPANT)}
                        name="role-participant"
                        aria-label="Select participant role"
                        marginRight="0.75rem"
                        label = ""
                      />
                      <Flex direction="column">
                        <Text fontWeight="bold" color="white">{TripRole.PARTICIPANT}</Text>
                        <Text color="#A0AEC0" fontSize="0.875rem">
                          Can upload and delete from the album
                        </Text>
                      </Flex>
                    </Flex>
                  </View>
                  
                  <View 
                    padding="1rem"
                    backgroundColor={selectedRole === TripRole.VIEWER ? "#1E3A8A" : "rgba(45, 55, 72, 0.5)"}
                    borderRadius="medium"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor={selectedRole === TripRole.VIEWER ? "#3B82F6" : "#4A5568"}
                    onClick={() => setSelectedRole(TripRole.VIEWER)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Flex direction="row" alignItems="center">
                      <CheckboxField
                        checked={selectedRole === TripRole.VIEWER}
                        onChange={() => setSelectedRole(TripRole.VIEWER)}
                        name="role-viewer"
                        aria-label="Select Viewer role"
                        marginRight="0.75rem"
                        label = ""
                      />
                      <Flex direction="column">
                        <Text fontWeight="bold" color="white">{TripRole.VIEWER}</Text>
                        <Text color="#A0AEC0" fontSize="0.875rem">
                          Can only view the album content
                        </Text>
                      </Flex>
                    </Flex>
                  </View>
                </Flex>
              </View>
              
              <View 
                backgroundColor="rgba(246, 173, 85, 0.1)" 
                borderRadius="medium"
                padding="1rem"
                marginTop="1.5rem"
                border="1px solid rgba(246, 173, 85, 0.3)"
              >
                <Flex alignItems="center">
                  <Link size={20} className="text-yellow-400 mr-2" />
                  <Text fontSize="0.875rem" color="#F6AD55">
                    Anyone with this link can join your album with {selectedRole.toLowerCase()} permissions.
                    The link will expire after 24 hours.
                  </Text>
                </Flex>
              </View>
              
              {error && (
                <View 
                  backgroundColor="rgba(245, 101, 101, 0.1)" 
                  borderRadius="medium"
                  padding="1rem"
                  marginTop="1rem"
                  border="1px solid rgba(245, 101, 101, 0.3)"
                >
                  <Text color="#F56565">{error}</Text>
                </View>
              )}
            </>
          ) : (
            <>
              <Flex direction="column" alignItems="center" marginBottom="1.5rem">
              <View 
                width="4rem" 
                height="4rem" 
                backgroundColor="rgba(66, 153, 225, 0.2)" 
                borderRadius="50%"
                marginBottom="1rem"
                style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                }}
                >
                <Link size={28} className="text-blue-400" />
              </View>
                <Heading level={4} color="white" marginBottom="0.5rem">Invitation Link Created!</Heading>
                <Text color="#A0AEC0" marginBottom="0.5rem">
                  Anyone with this link can join your album as a {selectedRole.toLowerCase()}
                </Text>
                <Text fontSize="0.75rem" color="#718096">Link expires in 24 hours</Text>
              </Flex>
              
              <View position="relative" marginBottom="1.5rem" style={{ position: 'relative' }}>
                <TextField
                  value={magicLink}
                  readOnly
                  backgroundColor="#2D3748"
                  color="white"
                  borderColor="#4A5568"
                  width="100%"
                  label=""
                />
                <Button 
                  variation="link"
                  onClick={copyLinkToClipboard}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '0.5rem',
                    padding: '0.25rem'
                  }}
                >
                  {linkCopied ? <Check size={18} className="text-green-500" /> : <Copy size={18} className="text-gray-400" />}
                </Button>
              </View>
            </>
          )}
        </View>
        
        <Divider />
        
        {/* Footer */}
        <Flex justifyContent="flex-end" gap="1rem" padding="1rem">
          <Button
            onClick={handleClose}
            variation="link"
          >
            {linkGenerated ? 'Done' : 'Cancel'}
          </Button>
          
          {!linkGenerated ? (
            <Button 
              onClick={generateMagicLink}
              isLoading={isLoading}
              loadingText="Generating..."
              variation="primary"
            >
              <Flex alignItems="center">
                <Link size={16} className="mr-2" />
                Generate Link
              </Flex>
            </Button>
          ) : (
            <Button 
              onClick={shareLink}
              variation="primary"
            >
              <Flex alignItems="center">
                <Share2 size={16} className="mr-2" />
                Share Link
              </Flex>
            </Button>
          )}
        </Flex>
      </Card>
    </div>
  );
};

export default InviteLinkModal;